import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert, Box, Button, Card, CardContent, Divider, FormControlLabel, IconButton,
  InputAdornment, Stack, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { callAction } from './dataProvider';

/**
 * Correcting an order.
 *
 * An order is a record of something that happened, and the rest of this screen
 * treats it that way. But records contain mistakes - a quantity picked wrong, a
 * price agreed on the phone, a line that was never actually sent - and refusing
 * to fix them here just moves the correction somewhere nobody can see it.
 *
 * So editing exists and is deliberately hard to do by accident: everything is
 * read-only until the switch is thrown, the switch says what it does, and
 * nothing is sent until Save. Changing an order never touches the product or
 * the price list - the line carries its own price and description.
 */

interface Line {
  id: number;
  code: string | null;
  name: string | null;
  quantity: number;
  unitPrice?: number | null;
}

type Order = { id: number | string; lines?: Line[]; note?: string | null };

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });

/** Only reached if a row appears after the draft was taken; keeps the spread total. */
const EMPTY_DRAFT = { quantity: '', unitPrice: '', remove: false };

export function OrderEditor() {
  const record = useRecordContext<Order>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [unlocked, setUnlocked] = useState(false);
  const [draft, setDraft] = useState<Record<number, { quantity: string; unitPrice: string; remove: boolean }>>({});
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (record == null || record.lines == null) return null;

  const lines = record.lines;

  const start = (on: boolean) => {
    setUnlocked(on);
    if (on) {
      setDraft(Object.fromEntries(lines.map(line => [
        line.id,
        {
          quantity: String(line.quantity),
          unitPrice: line.unitPrice == null ? '' : String(line.unitPrice),
          remove: false
        }
      ])));
      setNote(record.note ?? '');
    } else {
      setDraft({});
      setNote(null);
    }
  };

  const set = (id: number, field: 'quantity' | 'unitPrice', value: string) =>
    setDraft(current => ({
      ...current,
      [id]: { ...(current[id] ?? EMPTY_DRAFT), [field]: value }
    }));

  const toggleRemove = (id: number) =>
    setDraft(current => {
      const entry = current[id] ?? EMPTY_DRAFT;
      return { ...current, [id]: { ...entry, remove: !entry.remove } };
    });

  const changed = lines.filter(line => {
    const entry = draft[line.id];
    if (entry == null) return false;
    if (entry.remove) return true;

    const price = line.unitPrice == null ? '' : String(line.unitPrice);
    return entry.quantity !== String(line.quantity) || entry.unitPrice !== price;
  });

  const noteChanged = note != null && note !== (record.note ?? '');

  const save = async () => {
    setSaving(true);
    try {
      await callAction(`orders/${record.id}`, 'PATCH' as never, {
        ...(noteChanged ? { note } : {}),
        lines: changed.map(line => {
          const entry = draft[line.id] as { quantity: string; unitPrice: string; remove: boolean };
          if (entry.remove) return { id: line.id, remove: true };

          return {
            id: line.id,
            quantity: Number(entry.quantity),
            unitPrice: entry.unitPrice.trim() === '' ? null : Number(entry.unitPrice.replace(',', '.'))
          };
        })
      });

      notify('Objednávka upravená.', { type: 'success' });
      start(false);
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Objednávku sa nepodarilo upraviť.', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" color="text.secondary">Úprava objednávky</Typography>
          <FormControlLabel
            control={<Switch checked={unlocked} onChange={event => start(event.target.checked)} />}
            label="Povoliť úpravy"
          />
        </Stack>

        {!unlocked ? (
          <Typography variant="body2" color="text.secondary">
            Objednávka je záznam o tom, čo sa stalo, preto je zamknutá. Ak treba opraviť množstvo
            alebo cenu, zapnite prepínač.
          </Typography>
        ) : (
          <>
            <Alert severity="warning" variant="outlined" sx={{ my: 1 }}>
              Meníte záznam o už uskutočnenej objednávke. Zmena sa nedotkne produktov ani cenníka —
              upraví sa len táto objednávka a jej suma sa prepočíta.
            </Alert>

            <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
              {lines.map(line => {
                const entry = draft[line.id];
                if (entry == null) return null;

                return (
                  <Stack
                    key={line.id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 1, opacity: entry.remove ? 0.45 : 1 }}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ textDecoration: entry.remove ? 'line-through' : 'none' }}
                      >
                        {line.name ?? line.code ?? `#${line.id}`}
                      </Typography>
                      {line.code != null && (
                        <Typography variant="caption" color="text.secondary">{line.code}</Typography>
                      )}
                    </Box>

                    <TextField
                      label="Počet"
                      size="small"
                      value={entry.quantity}
                      disabled={entry.remove}
                      onChange={event => set(line.id, 'quantity', event.target.value)}
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="Cena / ks"
                      size="small"
                      value={entry.unitPrice}
                      disabled={entry.remove}
                      onChange={event => set(line.id, 'unitPrice', event.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                      sx={{ width: 140 }}
                    />

                    <Tooltip title={entry.remove ? 'Vrátiť späť' : 'Odstrániť z objednávky'}>
                      <IconButton size="small" onClick={() => toggleRemove(line.id)}>
                        {entry.remove ? <UndoIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>

            <TextField
              label="Poznámka k objednávke"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={note ?? ''}
              onChange={event => setNote(event.target.value)}
              sx={{ mt: 2 }}
            />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || (changed.length === 0 && !noteChanged)}
                onClick={() => void save()}
              >
                Uložiť zmeny
              </Button>
              <Button onClick={() => start(false)} disabled={saving}>Zrušiť</Button>

              {changed.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Zmenených riadkov: {changed.length}
                  {' · '}
                  nová suma bez DPH{' '}
                  {EUR.format(estimate(lines, draft))}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** What the order would come to, so the effect is visible before saving. */
function estimate(
  lines: readonly Line[],
  draft: Record<number, { quantity: string; unitPrice: string; remove: boolean }>
): number {
  return lines.reduce((sum, line) => {
    const entry = draft[line.id];
    if (entry == null || entry.remove) return sum;

    const price = Number(entry.unitPrice.replace(',', '.'));
    const quantity = Number(entry.quantity);
    if (!Number.isFinite(price) || !Number.isFinite(quantity)) return sum;

    return sum + price * quantity;
  }, 0);
}
